<!--
  Richiesta di prenotazione — dettaglio.

  ⚠️ NON è la pagina di una prenotazione: una richiesta vive su
  `booking_request`, non ha tratte né driver, e diventa un booking solo con
  la conversione (che valorizza `bookingId`). Da lì in poi si lavora sulla
  pagina del booking.

  Impostazione da design system: due colonne, contenuto a sinistra e
  riepilogo sticky a destra. Le azioni di stato stanno nell'intestazione.

  ⚠️⚠️ TRAPPOLA DELLA MACCHINA A STATI — l'assegnazione del partner è
  ammessa SOLO da `pending` / `counter_proposed`, mentre la conversione
  pretende partner E sede. Quindi approvare una richiesta senza partner la
  congela per sempre: non si può più assegnare (assign rifiuta), non si può
  convertire (manca il partner) e non si può nemmeno rifiutare (reject non
  parte da `approved`). Per questo qui il pulsante Approva è BLOCCATO finché
  partner e sede non ci sono, e la cosa è detta a schermo.

  Il backend non tiene uno storico: contro-proposte e motivi di rifiuto
  vengono APPESI al campo `note` con i prefissi [contro-proposta] / [rifiuto].
  La card delle note li separa dal testo scritto dal cliente.
-->
<template>
  <div
    v-loading="loading"
    class="brq"
  >
    <!-- ---------- intestazione ---------- -->
    <div class="brq__head">
      <button
        type="button"
        class="brq__back"
        @click="goBack"
      >
        ‹
      </button>
      <div class="brq__ident">
        <h1 class="brq__title">
          Richiesta di prenotazione
          <StatusPill
            :kind="state.kind"
            :label="state.label"
          />
        </h1>
        <div class="brq__sub">
          <ServiceChip :service="chipService" />
          <span class="brq__id">#{{ shortId }}</span>
          <span v-if="request.submitted_at">
            · ricevuta {{ dtLabel(request.submitted_at) }} ({{ waitLabel }})
          </span>
        </div>
      </div>
      <div class="brq__spacer" />

      <!-- Le azioni disponibili dipendono dallo stato: quelle non ammesse
           dal backend non si mostrano, invece di far scoprire il 409. -->
      <div class="brq__actions">
        <template v-if="request.bookingId">
          <el-button
            type="primary"
            @click="openBooking"
          >
            Apri la prenotazione
          </el-button>
        </template>
        <template v-else-if="isOpen">
          <el-button
            type="danger"
            plain
            :loading="busy === 'reject'"
            @click="rejectDialog = true"
          >
            Rifiuta
          </el-button>
          <el-button
            :loading="busy === 'counter'"
            @click="openCounter"
          >
            Contro-proponi
          </el-button>
          <el-tooltip
            :disabled="canApprove"
            content="Assegna prima partner e sede: dopo l'approvazione non è più possibile, e senza partner la richiesta non si può convertire."
            placement="bottom"
          >
            <span>
              <el-button
                type="primary"
                :disabled="!canApprove"
                :loading="busy === 'approve'"
                @click="openApprove"
              >
                Approva
              </el-button>
            </span>
          </el-tooltip>
        </template>
        <template v-else-if="request.status === 'approved'">
          <el-button
            type="primary"
            :disabled="!canConvert"
            :loading="busy === 'convert'"
            @click="openConvert"
          >
            Converti in prenotazione
          </el-button>
        </template>
      </div>
    </div>

    <!-- Il vicolo cieco va detto prima che l'operatore ci finisca dentro. -->
    <div
      v-if="isOpen && !canApprove"
      class="warnbox"
    >
      <span class="warnbox__ico">⚠</span>
      <div class="warnbox__txt">
        <b>Assegna partner e sede prima di approvare.</b>
        L'assegnazione è ammessa solo finché la richiesta è da valutare o in
        contro-proposta. Una richiesta approvata senza partner non si può più
        assegnare, né convertire, né rifiutare.
      </div>
    </div>
    <div
      v-if="request.status === 'approved' && !canConvert && !request.bookingId"
      class="warnbox warnbox--dead"
    >
      <span class="warnbox__ico">⚠</span>
      <div class="warnbox__txt">
        <b>Richiesta bloccata.</b>
        È approvata ma senza {{ missingForConvert.join(' e ') }}: da questo
        stato il backend non permette più né l'assegnazione né il rifiuto. Va
        sistemata a database o rifatta.
      </div>
    </div>

    <div class="brq__grid">
      <!-- ================= COLONNA PRINCIPALE ================= -->
      <div class="brq__col">
        <!-- Servizio -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Servizio richiesto</h3>
          </header>
          <div class="brq__cardBody">
            <div class="brq__fields">
              <div class="brq__field">
                <span class="brq__label">Servizio</span>
                <span class="brq__value">{{ serviceLabel }}</span>
              </div>
              <div class="brq__field">
                <span class="brq__label">Tratta</span>
                <span class="brq__value">{{ legLabel }}</span>
              </div>
              <div
                v-if="isTransfer"
                class="brq__field"
              >
                <span class="brq__label">Mezzo</span>
                <span class="brq__value">{{ vehicleLabel || '—' }}</span>
              </div>
              <div
                v-if="isTransfer"
                class="brq__field"
              >
                <span class="brq__label">Passeggeri</span>
                <span class="brq__value">{{ request.passengers || '—' }}</span>
              </div>
              <!-- L'auto scelta dal cliente nell'app. Il backend la carica già
                   con la richiesta (`booking_request.car`), ma la pagina non
                   la mostrava: chi valutava la richiesta non sapeva su quale
                   auto lavorare. -->
              <div
                v-if="request.car"
                class="brq__field"
              >
                <span class="brq__label">Auto</span>
                <span class="brq__value">
                  {{ [request.car.marca, request.car.modello].filter(Boolean).join(' ') || 'Auto' }}
                  <template v-if="request.car.targa">· {{ request.car.targa }}</template>
                </span>
              </div>
              <div
                v-if="request.loaner_requested"
                class="brq__field brq__field--full"
              >
                <span class="brq__label">Auto di cortesia</span>
                <span class="brq__value">Richiesta dal cliente</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Percorso e orari -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Percorso e orari</h3>
          </header>
          <div class="brq__cardBody">
            <article
              v-for="leg in legs"
              :key="leg.key"
              class="brq__leg"
            >
              <div class="brq__legHead">
                <span
                  class="brq__legTag"
                  :class="`brq__legTag--${leg.key}`"
                >{{ leg.title }}</span>
                <span class="brq__legWhen">
                  {{ dtLabel(leg.at) || 'Data e ora non indicate' }}
                  <!-- L'arrivo NON è un dato della richiesta: il cliente
                       indica solo quando vuole partire. È una stima, e va
                       detto — vedi `durataTragitto`. -->
                  <em
                    v-if="leg.at && arrivoStimato(leg)"
                    class="brq__eta"
                  >· arrivo stimato {{ arrivoStimato(leg) }}</em>
                </span>
              </div>
              <div class="brq__route">
                <div class="brq__pt">
                  <span class="brq__pin brq__pin--from" />
                  <span class="brq__addr">{{ leg.from || '—' }}</span>
                </div>
                <div class="brq__pt">
                  <span class="brq__pin brq__pin--to" />
                  <span class="brq__addr">{{ leg.to || '—' }}</span>
                </div>
              </div>
            </article>
            <p
              v-if="!legs.length"
              class="brq__empty"
            >
              Nessuna data richiesta.
            </p>
          </div>
        </section>

        <!-- Note + storico appeso dal backend -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Note</h3>
          </header>
          <div class="brq__cardBody">
            <p
              v-if="notes.own"
              class="brq__note"
            >
              {{ notes.own }}
            </p>
            <p
              v-else
              class="brq__empty"
            >
              Il cliente non ha lasciato note.
            </p>

            <div
              v-if="notes.events.length"
              class="brq__events"
            >
              <span class="brq__eventsLbl">Storico delle decisioni</span>
              <div
                v-for="(ev, i) in notes.events"
                :key="i"
                class="brq__event"
                :class="`brq__event--${ev.kind}`"
              >
                <span class="brq__eventKind">{{ ev.kind === 'rifiuto' ? 'Rifiuto' : 'Contro-proposta' }}</span>
                <span class="brq__eventTxt">{{ ev.text || '—' }}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- ================= RIEPILOGO STICKY ================= -->
      <aside class="brq__aside">
        <!-- Cliente -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Cliente</h3>
          </header>
          <div class="brq__cardBody">
            <div class="brq__cust">
              <span class="brq__avatar">{{ initials }}</span>
              <div>
                <div class="brq__custName">
                  {{ customerName }}
                </div>
                <div
                  v-if="!request.customerId"
                  class="brq__muted"
                >
                  Non registrato: solo recapito
                </div>
              </div>
            </div>
            <div class="brq__kv">
              <span>Telefono</span>
              <b>{{ request.customer?.telephone_number || (contactIsMail ? '' : request.contact) || '—' }}</b>
            </div>
            <div class="brq__kv">
              <span>Email</span>
              <b>{{ request.customer?.mail || (contactIsMail ? request.contact : '') || '—' }}</b>
            </div>
            <el-button
              v-if="request.customerId"
              size="small"
              class="brq__wide"
              @click="openCustomer"
            >
              Apri scheda cliente
            </el-button>
          </div>
        </section>

        <!-- Assegnazione -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Partner e sede</h3>
          </header>
          <div class="brq__cardBody">
            <template v-if="request.partnerId">
              <div class="brq__kv">
                <span>Partner</span>
                <b>{{ partner?.company_name || '—' }}</b>
              </div>
              <div class="brq__kv">
                <span>Sede</span>
                <b :class="{ 'brq__attn': !request.headquarterId }">
                  {{ headquarterLabel }}
                </b>
              </div>
              <p
                v-if="assignedHq"
                class="brq__hqAddr"
              >
                {{ fullAddress(assignedHq) || 'Indirizzo non indicato' }}
              </p>
            </template>
            <p
              v-else
              class="brq__empty brq__attn"
            >
              Nessun partner assegnato. Succede alle richieste nate senza
              dealer: notturno con partenza libera e transfer.
            </p>
            <el-button
              v-if="isOpen"
              size="small"
              class="brq__wide"
              @click="openAssign"
            >
              {{ request.partnerId ? 'Cambia partner o sede' : 'Assegna partner e sede' }}
            </el-button>
          </div>
        </section>

        <!-- Prezzo: ricalcolato con partner e sede attuali (vedi loadPrice) -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Prezzo</h3>
            <span
              v-if="price?.mode === 'quoted' && price.indicative"
              class="brq__tag"
            >indicativo</span>
          </header>
          <div
            v-loading="priceLoading"
            class="brq__cardBody"
          >
            <div
              v-for="row in priceRows.filter((r) => !r.approveOnly)"
              :key="row.label"
              class="brq__kv"
              :class="{ 'brq__kv--strong': row.strong }"
            >
              <span>{{ row.label }}</span>
              <b>{{ row.value }}</b>
            </div>
            <p
              v-for="(n, i) in priceNotes"
              :key="i"
              class="brq__hint"
              :class="{ 'brq__attn': n.attn }"
            >
              {{ n.text }}
            </p>
          </div>
        </section>

        <!-- Requisiti della conversione -->
        <section class="brq__card">
          <header class="brq__cardHead">
            <h3>Conversione in prenotazione</h3>
          </header>
          <div class="brq__cardBody">
            <div
              v-for="c in checklist"
              :key="c.label"
              class="brq__check"
              :class="{ 'is-ok': c.ok, 'is-info': c.info }"
            >
              <span class="brq__checkIco">{{ c.info ? 'i' : c.ok ? '✓' : '○' }}</span>
              <span>{{ c.label }}</span>
            </div>
            <p
              v-if="request.bookingId"
              class="brq__done"
            >
              Convertita: la prenotazione esiste.
            </p>
          </div>
        </section>
      </aside>
    </div>

    <!-- ---------- assegnazione ---------- -->
    <el-dialog
      v-model="assignDialog"
      title="Assegna partner e sede"
      width="480px"
    >
      <div class="brq__form">
        <div class="brq__formField">
          <label>Partner</label>
          <el-select
            v-model="assignForm.partnerId"
            placeholder="Scegli il partner"
            filterable
            style="width: 100%"
            @change="onAssignPartnerChange"
          >
            <el-option
              v-for="p in partners"
              :key="p.id"
              :label="p.company_name"
              :value="p.id"
            />
          </el-select>
        </div>
        <div class="brq__formField">
          <label>Sede</label>
          <!-- Elenco e non tendina: le sedi di un partner hanno spesso nomi
               simili, e senza l'indirizzo completo non si distinguono. -->
          <div
            v-loading="assignHqLoading"
            class="brq__hqList"
          >
            <p
              v-if="!assignForm.partnerId"
              class="brq__empty"
            >
              Scegli prima il partner.
            </p>
            <p
              v-else-if="!assignHqLoading && !assignHeadquarters.length"
              class="brq__empty brq__attn"
            >
              Questo partner non ha sedi: aggiungine una.
            </p>
            <button
              v-for="h in assignHeadquarters"
              :key="h.id"
              type="button"
              class="brq__hqOpt"
              :class="{ 'is-on': assignForm.headquarterId === h.id }"
              @click="assignForm.headquarterId = h.id"
            >
              <span class="brq__hqRadio" />
              <span class="brq__hqTxt">
                <b>{{ h.name || 'Sede' }}</b>
                <span>{{ fullAddress(h) || 'Indirizzo non indicato' }}</span>
                <!-- I driver si cercano per provincia della sede: la vecchia
                     «area» non conta più. -->
                <span
                  v-if="!(h.provinceId || h.province_ref?.id)"
                  class="brq__attn"
                >⚠ sede senza provincia: niente orari prenotabili</span>
              </span>
            </button>
          </div>
          <el-button
            size="small"
            class="brq__wide"
            :disabled="!assignForm.partnerId"
            @click="newHqDialog = true"
          >
            ＋ Nuova sede per questo partner
          </el-button>
          <p class="brq__hint">
            La conversione pretende anche la sede, transfer compreso.
          </p>
        </div>
      </div>
      <template #footer>
        <el-button @click="assignDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :disabled="!assignForm.partnerId"
          :loading="busy === 'assign'"
          @click="doAssign"
        >
          Assegna
        </el-button>
      </template>
    </el-dialog>

    <!-- ---------- contro-proposta ---------- -->
    <el-dialog
      v-model="counterDialog"
      title="Contro-proposta"
      width="520px"
    >
      <p class="brq__hint brq__hint--top">
        Le date che lasci vuote restano quelle chieste dal cliente. La
        richiesta torna a lui: se accetta diventa approvata.
      </p>
      <div class="brq__form">
        <div class="brq__formRow">
          <div class="brq__formField">
            <label>Data andata</label>
            <el-date-picker
              v-model="counterForm.outbound_date"
              type="date"
              placeholder="gg/mm/aaaa"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </div>
          <div class="brq__formField">
            <label>Ora andata</label>
            <el-time-picker
              v-model="counterForm.outbound_time"
              placeholder="hh:mm"
              format="HH:mm"
              value-format="HH:mm"
              style="width: 100%"
            />
          </div>
        </div>
        <div class="brq__formRow">
          <div class="brq__formField">
            <label>Data ritorno</label>
            <el-date-picker
              v-model="counterForm.return_date"
              type="date"
              placeholder="gg/mm/aaaa"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </div>
          <div class="brq__formField">
            <label>Ora ritorno</label>
            <el-time-picker
              v-model="counterForm.return_time"
              placeholder="hh:mm"
              format="HH:mm"
              value-format="HH:mm"
              style="width: 100%"
            />
          </div>
        </div>
        <div class="brq__formField">
          <label>Motivazione per il cliente</label>
          <el-input
            v-model="counterForm.note"
            type="textarea"
            :rows="3"
            maxlength="2000"
            show-word-limit
            placeholder="Es. a quell'ora non abbiamo driver disponibili…"
          />
        </div>
      </div>
      <template #footer>
        <el-button @click="counterDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :disabled="!counterChanged"
          :loading="busy === 'counter'"
          @click="doCounter"
        >
          Invia contro-proposta
        </el-button>
      </template>
    </el-dialog>

    <!-- ---------- rifiuto ---------- -->
    <el-dialog
      v-model="rejectDialog"
      title="Rifiuta la richiesta"
      width="460px"
    >
      <p class="brq__hint brq__hint--top">
        Il rifiuto è definitivo: da questo stato la richiesta non si riapre.
      </p>
      <div class="brq__formField">
        <label>Motivo (facoltativo)</label>
        <el-input
          v-model="rejectReason"
          type="textarea"
          :rows="3"
          maxlength="2000"
          show-word-limit
        />
      </div>
      <template #footer>
        <el-button @click="rejectDialog = false">
          Annulla
        </el-button>
        <el-button
          type="danger"
          :loading="busy === 'reject'"
          @click="doReject"
        >
          Rifiuta
        </el-button>
      </template>
    </el-dialog>

    <!-- ---------- nuova sede (dal dialogo di assegnazione) ---------- -->
    <HeadquarterDialog
      v-model="newHqDialog"
      :partner-id="assignForm.partnerId"
      @saved="onHqCreated"
    />

    <!-- ---------- approvazione ---------- -->
    <!-- Prima di approvare si vede quanto pagherà il cliente: dopo
         l'approvazione la richiesta non si modifica più. -->
    <el-dialog
      v-model="approveDialog"
      title="Approva la richiesta"
      width="460px"
    >
      <div v-loading="priceLoading">
        <div
          v-for="row in priceRows"
          :key="row.label"
          class="brq__kv"
          :class="{ 'brq__kv--strong': row.strong }"
        >
          <span>{{ row.label }}</span>
          <b>{{ row.value }}</b>
        </div>
        <p
          v-for="(n, i) in priceNotes"
          :key="i"
          class="brq__hint"
          :class="{ 'brq__attn': n.attn }"
        >
          {{ n.text }}
        </p>
      </div>
      <template #footer>
        <el-button @click="approveDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="busy === 'approve'"
          @click="confirmApprove"
        >
          Approva
        </el-button>
      </template>
    </el-dialog>

    <!-- ---------- conversione ---------- -->
    <!-- Una finestra sola con tutto quello che serve alla conversione:
         importi, pagamento in app, compenso driver e copertura. Prima erano
         nove blocchi in fila senza gerarchia né spaziature, in 480px. -->
    <el-dialog
      v-model="convertDialog"
      title="Converti in prenotazione"
      width="min(680px, 94vw)"
    >
      <div class="cvt">
        <p class="cvt__intro">
          Nasce una prenotazione vera, con le tratte di andata e ritorno. La
          richiesta resta come traccia e non è più modificabile.
        </p>

        <!-- IMPORTI. Il preventivo della richiesta è una stima: qui si
             conferma o si corregge. Vuoti = la prenotazione nasce senza
             importi e si scrivono dal suo dettaglio. -->
        <section class="cvt__sec">
          <h4 class="cvt__secT">
            Importi
          </h4>
          <div class="cvt__grid">
            <div
              v-if="isTransfer"
              class="cvt__fld"
            >
              <label>Prezzo cliente</label>
              <el-input
                v-model="convertPrice"
                placeholder="Es. 180"
              >
                <template #prepend>
                  €
                </template>
              </el-input>
            </div>
            <div class="cvt__fld">
              <label>Imponibile</label>
              <el-input
                v-model="convertImponibile"
                placeholder="IVA esclusa"
                aria-label="Imponibile della prenotazione"
              >
                <template #prepend>
                  €
                </template>
              </el-input>
            </div>
            <div class="cvt__fld">
              <label>Totale fattura</label>
              <el-input
                v-model="convertTotale"
                placeholder="IVA inclusa"
                aria-label="Totale fattura della prenotazione"
              >
                <template #prepend>
                  €
                </template>
              </el-input>
            </div>
          </div>
          <p class="cvt__hint">
            Precompilati col preventivo, quando c'è; restano modificabili dal
            dettaglio della prenotazione.
            <template v-if="isTransfer">
              Sul transfer il prezzo al cliente è commerciale: lo decidi tu, il
              motore tariffe non lo calcola.
            </template>
          </p>
        </section>

        <!-- PAGAMENTO IN APP: diventa una voce da pagare sulla prenotazione,
             quella che il cliente salda dall'app (e che il COD5 conta). -->
        <section class="cvt__sec">
          <h4 class="cvt__secT">
            Pagamento in app
          </h4>
          <el-checkbox v-model="convertPagaInApp">
            Il cliente paga una parte in app
          </el-checkbox>
          <template v-if="convertPagaInApp">
            <div class="cvt__grid cvt__grid--app">
              <div class="cvt__fld">
                <label>Importo</label>
                <el-input
                  v-model="convertAppImporto"
                  placeholder="Quanto paga in app"
                  aria-label="Importo che il cliente paga in app"
                >
                  <template #prepend>
                    €
                  </template>
                </el-input>
              </div>
              <div class="cvt__fld">
                <label>Descrizione</label>
                <el-input
                  v-model="convertAppDescrizione"
                  placeholder="Cosa paga (es. Acconto)"
                  aria-label="Descrizione del pagamento in app"
                />
              </div>
            </div>
            <p class="cvt__hint">
              Nasce da pagare: il cliente la salda dall'app, e finisce nel COD5.
            </p>
          </template>
        </section>

        <!-- COMPENSO DEL DRIVER, una cifra per gamba. Sul notturno si concorda
             qui: non dipende dalle ore, e il driver non è ancora assegnato —
             l'importo resta sulla gamba e lo prende chi verrà messo. -->
        <section class="cvt__sec">
          <h4 class="cvt__secT">
            Compenso driver
            <span
              v-if="isNotturno"
              class="cvt__secTag"
            >notturno</span>
          </h4>
          <div class="cvt__grid">
            <div
              v-for="leg in legs"
              :key="leg.key"
              class="cvt__fld"
            >
              <label>{{ leg.key === 'return' ? 'Ritorno' : 'Andata' }}</label>
              <el-input
                v-model="convertComp[leg.key]"
                placeholder="tariffa oraria"
                :aria-label="`Compenso driver ${leg.key === 'return' ? 'ritorno' : 'andata'}`"
              >
                <template #prepend>
                  €
                </template>
              </el-input>
            </div>
          </div>
          <p class="cvt__hint">
            Vuoto = il driver prende la tariffa oraria della sua agenzia. Sul
            notturno la cifra si concorda: scrivila qui.
          </p>
        </section>

        <!-- COPERTURA DRIVER. La conversione pianifica ogni gamba: se il
             planner non trova nessuno libero, fallisce e la richiesta resta
             dov'è. Quanti driver ci sono DAVVERO nella provincia della sede è
             il dato che spiega un «no jolly available». -->
        <section class="cvt__sec">
          <h4 class="cvt__secT">
            Copertura driver
          </h4>
          <div
            v-if="copertura"
            class="cvt__cover"
            :class="{ 'is-attn': serveJolly && !copertura.jolly }"
          >
            <span class="cvt__coverIco">{{ serveJolly && !copertura.jolly ? '⚠' : 'ⓘ' }}</span>
            <div>
              <b>{{ copertura.provincia || 'Provincia della sede' }}:</b>
              {{ copertura.driver }} driver operativi, di cui
              {{ copertura.jolly }} jolly.
              <template v-if="serveJolly && !copertura.jolly">
                Un ritiro vuole anche un jolly: senza, la conversione si ferma.
                Assegna un jolly a questa provincia, oppure spunta qui sotto.
              </template>
              <template v-else-if="serveJolly">
                Se la conversione dice «nessun jolly», a quell'ora sono tutti
                occupati: sposta l'orario o spunta qui sotto.
              </template>
            </div>
          </div>
          <el-checkbox v-model="convertForce">
            Converti anche senza copertura driver verificata
          </el-checkbox>
          <p class="cvt__hint">
            Spuntala se la conversione si ferma su «nessun driver» o «nessun
            jolly» per quell'ora: la prenotazione nasce con i planner scoperti
            e i driver si assegnano dal suo dettaglio.
          </p>
        </section>
      </div>
      <template #footer>
        <el-button @click="convertDialog = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="busy === 'convert'"
          @click="doConvert"
        >
          Converti
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import bookingRequestAPI, {
  requestState,
} from '../../services/bookingRequestAPI.js';
import { VEHICLE_LABELS } from '../../services/vehicleTypes.js';
import partnerAPI from '../../services/partnerAPI.js';
import bookingAPI from '../../services/bookingAPI.js';
import driverAPI from '../../services/driverAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';
import ServiceChip from '../../components/ui/ServiceChip.vue';
import HeadquarterDialog from '../../components/HeadquarterDialog.vue';

const SERVICE_TEXT = {
  pickup: 'Pickup & Delivery',
  pickup_loaner: 'Pickup & Delivery con auto di cortesia',
  restaurant: 'Notturno',
  escort: 'Transfer',
  transfer: 'Transfer',
};

const LEG_TEXT = {
  round_trip: 'Andata e ritorno',
  outbound_only: 'Solo andata',
  return_only: 'Solo ritorno',
};

// Indirizzo completo di una sede: via e civico, CAP, città e provincia.
const fullAddress = (h) => {
  if (!h) return '';
  const street = [h.address, h.street_number].filter(Boolean).join(' ');
  const city = [h.cap, h.city].filter(Boolean).join(' ');
  return [street, city && h.province ? `${city} (${h.province})` : city || h.province]
    .filter(Boolean).join(', ');
};

const euro = (n) =>
  n == null || n === '' || !Number.isFinite(Number(n))
    ? '—'
    : `€ ${Number(n).toFixed(2).replace('.', ',')}`;

const QUOTE_REASONS = {
  missing_points: 'Mancano gli indirizzi per calcolare la distanza.',
  no_tariff: 'Nessuna fascia del listino copre questa distanza.',
  distance_failed: 'Il calcolo della distanza non è riuscito.',
  unsupported_service: 'Per questo servizio il prezzo non si calcola in automatico.',
};

const place = (p) =>
  p ? [p.address, p.street_number, p.city].filter(Boolean).join(' ').trim() : '';

const joinDateTime = (date, time) =>
  date ? new Date(`${date}T${String(time || '00:00').slice(0, 5)}:00`) : null;

const DAY_MS = 24 * 60 * 60 * 1000;

// 409 = transizione non ammessa dalla macchina a stati. L'interceptor globale
// non lo intercetta (case `default`), quindi il messaggio lo mostra la pagina:
// è l'errore più informativo che il backend restituisce.
const showError = (e, fallback) => {
  const status = e?.response?.status;
  const detail = e?.response?.data?.message;
  if (status === 409 || status === 400) {
    ElMessage.error(Array.isArray(detail) ? detail.join(' · ') : detail || fallback);
  } else if (!e?.response) {
    ElMessage.error(fallback);
  }
};

export default {
  name: 'BookingRequestDetailPage',
  components: { StatusPill, ServiceChip, HeadquarterDialog },

  setup() {
    const route = useRoute();
    const router = useRouter();

    const request = ref({});
    const partner = ref(null);
    const partners = ref([]);
    const loading = ref(false);
    const busy = ref('');

    const assignDialog = ref(false);
    const counterDialog = ref(false);
    const rejectDialog = ref(false);
    const convertDialog = ref(false);
    const approveDialog = ref(false);
    const newHqDialog = ref(false);

    const assignForm = ref({ partnerId: '', headquarterId: '' });
    const assignHeadquarters = ref([]);
    const assignHqLoading = ref(false);
    const counterForm = ref({
      outbound_date: '', outbound_time: '', return_date: '', return_time: '', note: '',
    });
    const rejectReason = ref('');
    const convertPrice = ref('');
    // Importi della prenotazione e pagamento in app, decisi alla conversione.
    const convertImponibile = ref('');
    const convertTotale = ref('');
    const convertPagaInApp = ref(false);
    const convertForce = ref(false);
    // Driver della provincia della sede: stessa regola del planner.
    const copertura = ref(null);
    // Ritiri e notturni impegnano un conducente E un jolly; transfer ed
    // escort solo il conducente (vedi la conversione lato server).
    const serveJolly = computed(() => !['escort', 'transfer'].includes(request.value.service));

    const caricaCopertura = async () => {
      copertura.value = null;
      const hq = assignedHq.value;
      const provinceId = hq?.provinceId || hq?.province_ref?.id || null;
      if (!provinceId) return;
      try {
        const res = await driverAPI.getDriversByProvince(provinceId);
        const righe = res?.data?.data ?? res?.data ?? [];
        copertura.value = {
          provincia: hq?.province_ref?.sigla || hq?.province || hq?.city || '',
          driver: righe.length,
          jolly: righe.filter((d) => d.is_jolly).length,
        };
      } catch (e) {
        console.error('Copertura driver non caricata:', e);
      }
    };
    const convertAppImporto = ref('');
    const convertAppDescrizione = ref('');
    // Compenso del driver per gamba, deciso accettando la richiesta.
    const convertComp = reactive({ gone: '', return: '' });

    // ---- lettura ---------------------------------------------------------
    const state = computed(() => requestState(request.value.status));
    const shortId = computed(() => (request.value.id || '').slice(0, 8));

    // Il transfer delle richieste è l'escort dei booking: stesso servizio,
    // nome diverso (vedi ServiceChip).
    const isTransfer = computed(() =>
      ['transfer', 'escort'].includes(request.value.service));
    const isNotturno = computed(() => request.value.service === 'restaurant');
    const chipService = computed(() =>
      request.value.service === 'transfer' ? 'escort' : request.value.service || 'pickup');
    const serviceLabel = computed(() =>
      SERVICE_TEXT[request.value.service] || request.value.service || '—');
    const vehicleLabel = computed(() => VEHICLE_LABELS[request.value.vehicle_type] || '');

    const legLabel = computed(() => {
      const r = request.value;
      if (r.leg_type) return LEG_TEXT[r.leg_type] || r.leg_type;
      return r.return_requested ? 'Andata e ritorno' : 'Solo andata';
    });

    // Gli stati da cui il backend accetta ancora modifiche.
    const isOpen = computed(() =>
      ['pending', 'counter_proposed'].includes(request.value.status)
      && !request.value.bookingId);

    const canApprove = computed(() =>
      isOpen.value && !!request.value.partnerId && !!request.value.headquarterId);

    const missingForConvert = computed(() => {
      const out = [];
      if (!request.value.partnerId) out.push('partner');
      if (!request.value.headquarterId) out.push('sede');
      return out;
    });

    const canConvert = computed(() =>
      request.value.status === 'approved'
      && !request.value.bookingId
      && !missingForConvert.value.length);

    const checklist = computed(() => {
      const r = request.value;
      const items = [
        { label: 'Partner assegnato', ok: !!r.partnerId },
        { label: 'Sede assegnata', ok: !!r.headquarterId },
        { label: 'Richiesta approvata', ok: r.status === 'approved' },
      ];
      if (isTransfer.value) {
        items.push({ label: 'Prezzo cliente da inserire a mano', info: true });
      }
      return items;
    });

    // Sul pickup la sede del partner è l'altro capo della tratta; sul
    // notturno la partenza può essere la sede oppure un indirizzo libero.
    const hqPlace = computed(() => {
      const hq = (partner.value?.headquarters || [])
        .find((h) => h.id === request.value.headquarterId);
      return hq ? (place(hq) || hq.name || hq.city || 'Sede partner') : 'Sede partner';
    });

    const assignedHq = computed(() =>
      (partner.value?.headquarters || []).find((h) => h.id === request.value.headquarterId) || null);

    const headquarterLabel = computed(() => {
      if (!request.value.headquarterId) return 'Non assegnata';
      const hq = (partner.value?.headquarters || [])
        .find((h) => h.id === request.value.headquarterId);
      return hq ? hqLabel(hq) : '—';
    });

    function hqLabel(h) {
      return h?.name || h?.city || (h?.id || '').slice(0, 8);
    }

    // ------------------------------------------------------------------
    //  ORARIO DI ARRIVO — è una STIMA, non un dato della richiesta.
    //
    //  `booking_request` salva solo quando il cliente vuole PARTIRE
    //  (`outbound_time`, `return_time`): un orario di arrivo non esiste a
    //  database, e non potrebbe, perché dipende dalla durata del tragitto.
    //
    //  Si ricava come fa la pagina di creazione: si chiede al motore la
    //  durata fra i due capi e la si somma all'orario richiesto. Serve che
    //  entrambi i punti abbiano le coordinate — le richieste vecchie possono
    //  non averle. In quel caso non si mostra nulla: un arrivo inventato su
    //  una distanza non calcolata sarebbe peggio di un campo vuoto.
    //
    //  Una sola chiamata, al caricamento della pagina. In elenco non si fa:
    //  sarebbe una chiamata per riga.
    // ------------------------------------------------------------------
    const durataTragitto = ref(null);

    const caricaDurata = async () => {
      const r = request.value;
      const a = r.pickup_point;
      const b = r.dropoff_point;
      if (!r.headquarterId || !a?.latitude || !b?.latitude) return;
      try {
        const res = await bookingAPI.getRangeEscort({
          headquarter_id: r.headquarterId,
          origins_point: a,
          destination_point: b,
          loaner: false,
        });
        const min = Number(res?.data?.minute);
        durataTragitto.value = Number.isFinite(min) && min > 0 ? min : null;
      } catch (e) {
        // best-effort: senza durata la pagina resta com'era
        console.warn('Durata tragitto non calcolata:', e?.message);
      }
    };

    const arrivoStimato = (leg) => {
      if (!durataTragitto.value || !leg.at) return '';
      const partenza = new Date(leg.at);
      if (Number.isNaN(partenza.getTime())) return '';
      const arrivo = new Date(partenza.getTime() + durataTragitto.value * 60000);
      return `${String(arrivo.getHours()).padStart(2, '0')}:${String(arrivo.getMinutes()).padStart(2, '0')}`;
    };

    // Andata e ritorno come due tratte leggibili: il pickup ha un capo solo
    // (l'indirizzo del cliente), l'altro è la sede.
    const legs = computed(() => {
      const r = request.value;
      const out = [];
      const home = place(r.pickup_point);
      const dest = place(r.dropoff_point);
      const isPickup = ['pickup', 'pickup_loaner'].includes(r.service);
      const from = isPickup ? home : (home || hqPlace.value);
      const to = isPickup ? hqPlace.value : dest;

      if (r.leg_type !== 'return_only' && (r.outbound_date || !r.return_date)) {
        out.push({
          key: 'gone',
          title: '↗ Andata',
          at: joinDateTime(r.outbound_date, r.outbound_time),
          from,
          to,
        });
      }
      if (r.return_date || r.leg_type === 'round_trip' || r.leg_type === 'return_only') {
        out.push({
          key: 'return',
          title: '↘ Ritorno',
          at: joinDateTime(r.return_date, r.return_time),
          from: to,
          to: from,
        });
      }
      return out;
    });

    // Il backend appende contro-proposte e rifiuti dentro `note`: qui si
    // rimettono in fila, separati dal testo del cliente.
    const notes = computed(() => {
      const lines = String(request.value.note || '').split('\n');
      const own = [];
      const events = [];
      lines.forEach((line) => {
        const m = line.match(/^\[(contro-proposta|rifiuto)\]\s*(.*)$/);
        if (m) events.push({ kind: m[1], text: m[2] });
        else own.push(line);
      });
      return { own: own.join('\n').trim(), events };
    });

    const customerName = computed(() => {
      const c = request.value.customer;
      const n = [c?.name, c?.surname].filter(Boolean).join(' ');
      return n || request.value.contact || 'Cliente non registrato';
    });
    // Il recapito di chi non è registrato può essere un telefono o una mail.
    const contactIsMail = computed(() => /@/.test(request.value.contact || ''));

    const initials = computed(() =>
      customerName.value.trim().split(/\s+/).slice(0, 2)
        .map((w) => w[0]).join('').toUpperCase() || '?');

    const waitLabel = computed(() => {
      if (!request.value.submitted_at) return '';
      const days = Math.floor(
        (Date.now() - new Date(request.value.submitted_at).getTime()) / DAY_MS,
      );
      if (days <= 0) return 'oggi';
      return days === 1 ? 'da 1 giorno' : `da ${days} giorni`;
    });

    // ---- prezzo ----------------------------------------------------------
    //  Si ricalcola con partner e sede di adesso (il `quote` salvato è quello
    //  della creazione, spesso col listino di SIMBA MOBILITY). Se il ricalcolo
    //  non riesce si ripiega su quello salvato, dicendolo.
    //  Paga in app = totale fattura (IVA inclusa): è la cifra che vede il
    //  cliente. Con un pacchetto valet è la sola eccedenza km.
    const price = ref(null);
    const priceFromSaved = ref(false);
    const priceLoading = ref(false);
    const band = ref(null);

    const loadPrice = async () => {
      const r = request.value;
      if (!r.id) return;
      priceLoading.value = true;
      band.value = null;
      try {
        price.value = await bookingRequestAPI.quoteForRequest(r);
        priceFromSaved.value = false;
      } catch (e) {
        console.warn('Preventivo non ricalcolato:', e?.response?.data?.message || e?.message);
        price.value = r.quote || null;
        priceFromSaved.value = !!r.quote;
      }
      const q = price.value;
      if (q?.km != null && q.mode !== 'on_confirmation') {
        try {
          // Il listino è quello del partner della richiesta; senza partner il
          // backend usa SIMBA MOBILITY, che si riconosce dal nome.
          const simba = partners.value.find((p) => /simba\s*mobility/i.test(p.company_name || ''));
          const partnerId = q.scope === 'partner' ? r.partnerId
            : q.scope === 'simba' ? simba?.id : null;
          band.value = await bookingRequestAPI.bandFor({
            partnerId,
            service: q.service || r.service,
            km: q.km,
            date: String(r.outbound_date || r.return_date || new Date().toISOString()).slice(0, 10),
          });
        } catch (e) {
          console.warn('Fascia km non trovata:', e?.message);
        }
      }
      priceLoading.value = false;
    };

    const bandLabel = computed(() => {
      const b = band.value;
      if (!b) return '—';
      const km = (n) => Number(n).toLocaleString('it-IT');
      return b.max_km == null ? `oltre ${km(b.min_km)} km` : `${km(b.min_km)}–${km(b.max_km)} km`;
    });

    const priceRows = computed(() => {
      const q = price.value;
      if (!q || q.mode !== 'quoted') {
        return q?.km != null ? [{ label: 'Distanza', value: `${Number(q.km).toLocaleString('it-IT')} km` }] : [];
      }
      const net = Number(q.price_net);
      const gross = Number(q.price_gross);
      return [
        // solo nel riepilogo dell'approvazione, non nel riquadro a destra
        { label: 'Paga in app', value: euro(gross), strong: true, approveOnly: true },
        { label: 'Imponibile', value: euro(net) },
        { label: `IVA ${q.vat_rate ?? 22}%`, value: euro(gross - net) },
        { label: 'Totale fattura', value: euro(gross), strong: true },
        { label: 'Distanza', value: q.km != null ? `${Number(q.km).toLocaleString('it-IT')} km` : '—' },
        { label: 'Fascia km', value: bandLabel.value },
      ];
    });

    const priceNotes = computed(() => {
      const q = price.value;
      const out = [];
      if (priceLoading.value && !q) return out;
      if (!q) return [{ text: 'Prezzo non disponibile.', attn: true }];
      if (q.mode === 'on_confirmation') {
        out.push({ text: 'Transfer: il prezzo al cliente si decide alla conversione.' });
      } else if (q.mode === 'unavailable') {
        out.push({ text: QUOTE_REASONS[q.reason] || 'Prezzo non calcolabile.', attn: true });
      }
      if (q.mode === 'quoted' && q.scope === 'simba') {
        out.push({ text: 'Listino di SIMBA MOBILITY: assegna partner e sede per il prezzo definitivo.', attn: true });
      } else if (q.mode === 'quoted' && q.scope === 'global') {
        out.push({ text: 'Listino globale: il partner non ha una fascia sua per questa distanza.', attn: true });
      }
      if (q.valet) {
        out.push({
          text: q.valet.excess_km > 0
            ? `Corsa coperta dal pacchetto valet: paga solo l'eccedenza di ${q.valet.excess_km} km.`
            : 'Corsa inclusa nel pacchetto valet del cliente.',
        });
      }
      if (priceFromSaved.value) {
        out.push({ text: 'Ricalcolo non riuscito: è il preventivo visto dal cliente alla richiesta.', attn: true });
      }
      return out;
    });

    // ---- caricamento -----------------------------------------------------
    // GET :id unisce customer e punti, ma di partner e sede manda solo gli
    // id: il nome si legge dal dettaglio partner (che porta anche le sedi).
    const loadPartnerContext = async (partnerId) => {
      if (!partnerId) { partner.value = null; return; }
      try {
        const r = await partnerAPI.getPartnerDetail(partnerId);
        partner.value = r.data;
      } catch (e) {
        console.warn('Partner della richiesta non caricato:', e?.message);
        partner.value = null;
      }
    };

    const load = async () => {
      loading.value = true;
      try {
        const r = await bookingRequestAPI.getRequestById(route.params.id);
        request.value = r.data || {};
        await loadPartnerContext(request.value.partnerId);
        caricaDurata();
        loadPrice();
      } catch (e) {
        console.error('Richiesta non caricata:', e);
      } finally {
        loading.value = false;
      }
    };

    const loadPartners = async () => {
      try {
        const r = await partnerAPI.getPartners();
        partners.value = (r.data?.data ?? r.data ?? []).map((p) => ({
          id: p.id, company_name: p.company_name,
        }));
      } catch (e) {
        console.warn('Elenco partner non disponibile:', e?.message);
      }
    };

    // ---- azioni ----------------------------------------------------------
    const run = async (key, fn, okMsg) => {
      busy.value = key;
      try {
        await fn();
        ElMessage.success(okMsg);
        await load();
        return true;
      } catch (e) {
        showError(e, 'Operazione non riuscita.');
        return false;
      } finally {
        busy.value = '';
      }
    };

    const openAssign = async () => {
      assignForm.value = {
        partnerId: request.value.partnerId || '',
        headquarterId: request.value.headquarterId || '',
      };
      assignHeadquarters.value = partner.value?.headquarters || [];
      assignDialog.value = true;
      if (!partners.value.length) loadPartners();
    };

    const onAssignPartnerChange = async (id) => {
      assignForm.value.headquarterId = '';
      assignHeadquarters.value = [];
      if (!id) return;
      assignHqLoading.value = true;
      try {
        const r = await partnerAPI.getPartnerDetail(id);
        assignHeadquarters.value = r.data?.headquarters || [];
      } catch (e) {
        console.warn('Sedi del partner non caricate:', e?.message);
      } finally {
        assignHqLoading.value = false;
      }
    };

    const doAssign = async () => {
      const ok = await run(
        'assign',
        () => bookingRequestAPI.assignRequest(request.value.id, assignForm.value),
        'Partner assegnato.',
      );
      if (ok) assignDialog.value = false;
    };

    const openApprove = () => {
      approveDialog.value = true;
      // prezzo sempre fresco: partner o sede possono essere appena cambiati
      if (!priceLoading.value) loadPrice();
    };

    const confirmApprove = async () => {
      const ok = await run('approve', () => bookingRequestAPI.approveRequest(request.value.id),
        'Richiesta approvata.');
      if (ok) approveDialog.value = false;
    };

    // Sede appena creata dal dialogo di assegnazione: si ricaricano le sedi
    // del partner e si seleziona quella nuova.
    const onHqCreated = async (hq) => {
      await onAssignPartnerChange(assignForm.value.partnerId);
      if (hq?.id) assignForm.value.headquarterId = hq.id;
    };

    const openCounter = () => {
      const r = request.value;
      counterForm.value = {
        outbound_date: r.outbound_date || '',
        outbound_time: (r.outbound_time || '').slice(0, 5),
        return_date: r.return_date || '',
        return_time: (r.return_time || '').slice(0, 5),
        note: '',
      };
      counterDialog.value = true;
    };

    // Il backend ignora i campi vuoti: senza almeno una modifica la
    // contro-proposta cambierebbe solo lo stato.
    const counterChanged = computed(() => {
      const f = counterForm.value;
      const r = request.value;
      return !!f.note
        || f.outbound_date !== (r.outbound_date || '')
        || f.outbound_time !== (r.outbound_time || '').slice(0, 5)
        || f.return_date !== (r.return_date || '')
        || f.return_time !== (r.return_time || '').slice(0, 5);
    });

    const doCounter = async () => {
      const f = counterForm.value;
      const body = {};
      ['outbound_date', 'outbound_time', 'return_date', 'return_time', 'note']
        .forEach((k) => { if (f[k]) body[k] = f[k]; });
      const ok = await run(
        'counter',
        () => bookingRequestAPI.counterProposeRequest(request.value.id, body),
        'Contro-proposta inviata al cliente.',
      );
      if (ok) counterDialog.value = false;
    };

    const doReject = async () => {
      const ok = await run(
        'reject',
        () => bookingRequestAPI.rejectRequest(request.value.id, rejectReason.value),
        'Richiesta rifiutata.',
      );
      if (ok) { rejectDialog.value = false; rejectReason.value = ''; }
    };

    const openConvert = () => {
      convertPrice.value = '';
      convertComp.gone = '';
      convertComp.return = '';
      // Precompilati col preventivo quando c'è: sul transfer non c'è, e i
      // campi restano vuoti perché il prezzo lo decide l'operatore.
      const q = price.value;
      const quotato = q?.mode === 'quoted';
      convertImponibile.value = quotato && q.price_net != null ? String(q.price_net).replace('.', ',') : '';
      convertTotale.value = quotato && q.price_gross != null ? String(q.price_gross).replace('.', ',') : '';
      convertPagaInApp.value = false;
      convertForce.value = false;
      convertAppImporto.value = quotato && q.price_gross != null ? String(q.price_gross).replace('.', ',') : '';
      convertAppDescrizione.value = '';
      convertDialog.value = true;
      caricaCopertura();
    };

    // Importo scritto a mano, o `undefined` se il campo è vuoto (nessuna
    // decisione: paga il calcolo dell'agenzia).
    const importo = (v) => {
      const n = Number(String(v ?? '').replace(',', '.').trim());
      return String(v ?? '').trim() !== '' && Number.isFinite(n) && n >= 0 ? n : undefined;
    };

    const doConvert = async () => {
      const prezzoCliente = Number(String(convertPrice.value).replace(',', '.'));
      const compensi = {};
      for (const leg of legs.value) {
        const v = importo(convertComp[leg.key]);
        if (v !== undefined) compensi[leg.key] = v;
      }
      const imponibile = importo(convertImponibile.value);
      const totale = importo(convertTotale.value);
      const inApp = convertPagaInApp.value ? importo(convertAppImporto.value) : undefined;
      if (convertPagaInApp.value && !(inApp > 0)) {
        ElMessage.error('Scrivi quanto paga in app, o togli la spunta');
        return;
      }

      // La conversione crea la prenotazione; importi e pagamento in app si
      // scrivono subito dopo, sulla prenotazione appena nata. Se uno dei due
      // non riesce la prenotazione esiste lo stesso: si dice cosa manca,
      // invece di far credere che sia fallito tutto.
      busy.value = 'convert';
      let bookingId = null;
      try {
        const res = await bookingRequestAPI.convertRequest(request.value.id, {
          customerPrice: Number.isFinite(prezzoCliente) && prezzoCliente > 0 ? prezzoCliente : undefined,
          driverCompensation: Object.keys(compensi).length ? compensi : undefined,
          force: convertForce.value,
        });
        bookingId = res?.data?.bookingId ?? null;
      } catch (e) {
        showError(e, 'Conversione non riuscita.');
        busy.value = '';
        return;
      }

      const mancanti = [];
      try {
        if (imponibile !== undefined || totale !== undefined) {
          await bookingAPI.updateImporti(bookingId, {
            imponibile,
            tot_fattura: totale,
          });
        }
      } catch (e) {
        console.error('Importi non salvati:', e);
        mancanti.push('gli importi');
      }
      try {
        if (inApp > 0) {
          await bookingAPI.addPagamentoInApp({
            bookingId,
            partnerId: request.value.partnerId,
            descrizione: convertAppDescrizione.value.trim() || 'Pagamento in app',
            importo: inApp,
          });
        }
      } catch (e) {
        console.error('Pagamento in app non creato:', e);
        mancanti.push('il pagamento in app');
      }

      if (mancanti.length) {
        ElMessage.warning(
          `Prenotazione creata, ma ${mancanti.join(' e ')} non si sono salvati: aprila e scrivili dal suo dettaglio.`,
        );
      } else {
        ElMessage.success('Prenotazione creata.');
      }
      convertDialog.value = false;
      busy.value = '';
      await load();
    };

    // ---- navigazione e formattazione -------------------------------------
    const goBack = () => router.push('/booking');
    const openBooking = () => router.push(`/booking/detail/${request.value.bookingId}`);
    const openCustomer = () => router.push(`/customer/detail/${request.value.customerId}`);

    const dtLabel = (v) => {
      if (!v) return '';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      const day = d.toLocaleDateString('it-IT', {
        weekday: 'short', day: '2-digit', month: 'long', year: 'numeric',
      });
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${day} · ${hh}:${mm}`;
    };

    onMounted(async () => {
      await Promise.all([load(), loadPartners()]);
      // Senza partner la fascia si cerca sul listino di SIMBA MOBILITY, che
      // si riconosce dall'elenco partner: se è arrivato dopo, si ricalcola.
      if (price.value?.scope === 'simba' && !band.value) loadPrice();
    });

    return {
      request, partner, partners, loading, busy, contactIsMail,
      assignDialog, counterDialog, rejectDialog, convertDialog,
      convertImponibile, convertTotale, convertPagaInApp, convertForce,
      copertura, serveJolly,
      convertAppImporto, convertAppDescrizione,
      assignForm, assignHeadquarters, assignHqLoading,
      counterForm, counterChanged, rejectReason, convertPrice,
      state, shortId, chipService, serviceLabel, vehicleLabel, legLabel,
      isTransfer, isNotturno, convertComp, isOpen, canApprove, canConvert, missingForConvert, checklist,
      legs, arrivoStimato, notes, customerName, initials, waitLabel, headquarterLabel,
      openAssign, onAssignPartnerChange, doAssign, openApprove, confirmApprove,
      approveDialog, newHqDialog, onHqCreated, assignedHq, fullAddress,
      price, priceLoading, priceRows, priceNotes,
      openCounter, doCounter, doReject, openConvert, doConvert,
      goBack, openBooking, openCustomer, dtLabel, hqLabel,
    };
  },
};
</script>

<style scoped>
.brq { padding: 4px 0 32px; }
.brq__spacer { flex: 1; }

/* ---- intestazione ---- */
.brq__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 16px;
}
.brq__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.brq__back:hover { border-color: var(--brand-violet); }
.brq__ident { min-width: 0; }
.brq__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  flex-wrap: wrap; color: var(--text-primary);
}
.brq__sub {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-top: 5px; font-size: 12.5px; color: var(--text-tertiary);
}
.brq__id { font-variant-numeric: tabular-nums; color: var(--brand-violet); }
.brq__actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* ---- avvisi ---- */
.warnbox {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 13px; margin-bottom: 14px;
  border-radius: 9px; font-size: 12.5px; line-height: 1.5;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}
.warnbox--dead {
  color: var(--state-danger); background: var(--state-danger-bg);
  border-color: var(--state-danger-line);
}
.warnbox__ico { flex: none; font-size: 14px; line-height: 1.35; }
.warnbox__txt { flex: 1; }

/* ---- griglia ---- */
.brq__grid {
  display: grid; grid-template-columns: minmax(0, 1fr) 330px;
  gap: 16px; align-items: start;
}
@media (max-width: 960px) { .brq__grid { grid-template-columns: 1fr; } }
.brq__col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.brq__aside {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px;
}
@media (max-width: 960px) { .brq__aside { position: static; } }

/* ---- card ---- */
.brq__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.brq__cardHead {
  display: flex; align-items: center; gap: 10px; padding: 13px 16px;
  border-bottom: 1px solid var(--border-color);
}
.brq__cardHead h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.brq__cardBody { padding: 16px; }
.brq__empty { margin: 0; font-size: 12.5px; color: var(--text-tertiary); line-height: 1.5; }
.brq__muted { font-size: 11.5px; color: var(--text-tertiary); }
.brq__attn { color: var(--state-attn); }
.brq__wide { width: 100%; margin-top: 12px; }

/* ---- campi ---- */
.brq__fields {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 22px;
}
@media (max-width: 560px) { .brq__fields { grid-template-columns: 1fr; } }
.brq__field--full { grid-column: 1 / -1; }
.brq__label {
  display: block; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.brq__value {
  display: block; margin-top: 3px; font-size: 14px; font-weight: 600;
  color: var(--text-primary);
}

/* ---- tratte ---- */
.brq__leg {
  border: 1px solid var(--border-color); border-radius: var(--radius-md);
  padding: 12px 14px; background: var(--bg-secondary);
}
.brq__leg + .brq__leg { margin-top: 10px; }
.brq__legHead {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 10px;
}
.brq__legTag {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; padding: 2px 7px; border-radius: 5px;
  color: var(--state-muted); background: var(--state-muted-bg);
  border: 1px solid var(--state-muted-line);
}
.brq__legWhen {
  font-size: 13.5px; font-weight: 650; color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
/* L'arrivo è calcolato, non richiesto dal cliente: peso e colore più
   leggeri lo distinguono dall'orario che il cliente ha davvero indicato. */
.brq__eta {
  font-style: normal; font-weight: 500; color: var(--text-tertiary);
  margin-left: 4px;
}
.brq__route { display: flex; flex-direction: column; gap: 7px; }
.brq__pt { display: flex; align-items: flex-start; gap: 9px; }
.brq__pin { width: 9px; height: 9px; border-radius: 50%; margin-top: 5px; flex: none; }
.brq__pin--from { background: var(--text-tertiary); }
.brq__pin--to { background: var(--brand-violet); }
.brq__addr { font-size: 13px; line-height: 1.35; }

/* ---- note ---- */
.brq__note {
  margin: 0; font-size: 13.5px; line-height: 1.55; white-space: pre-wrap;
  color: var(--text-secondary);
}
.brq__events { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border-color); }
.brq__eventsLbl {
  display: block; margin-bottom: 8px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.brq__event {
  display: flex; gap: 10px; align-items: baseline; padding: 7px 0;
  border-bottom: 1px solid var(--border-color); font-size: 13px;
}
.brq__event:last-child { border-bottom: 0; }
.brq__eventKind {
  flex: none; font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; padding: 1px 7px; border-radius: 5px;
  color: var(--state-active); background: var(--state-active-bg);
  border: 1px solid var(--state-active-line);
}
.brq__event--rifiuto .brq__eventKind {
  color: var(--state-danger); background: var(--state-danger-bg);
  border-color: var(--state-danger-line);
}
.brq__eventTxt { color: var(--text-secondary); line-height: 1.45; }

/* ---- cliente ---- */
.brq__cust { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.brq__avatar {
  width: 40px; height: 40px; flex: none; border-radius: 50%;
  display: grid; place-items: center; background: var(--avatar-bg);
  color: #fff; font-weight: 700; font-size: 14px;
}
.brq__custName { font-weight: 650; color: var(--text-primary); }
.brq__kv {
  display: flex; justify-content: space-between; gap: 12px; padding: 6px 0;
  font-size: 13px; border-bottom: 1px solid var(--border-color);
}
.brq__kv:last-of-type { border-bottom: 0; }
.brq__kv span { color: var(--text-tertiary); }
.brq__kv b { color: var(--text-primary); font-weight: 650; text-align: right; }

/* ---- checklist conversione ---- */
.brq__check {
  display: flex; align-items: center; gap: 9px; padding: 6px 0;
  font-size: 13px; color: var(--text-tertiary);
}
.brq__checkIco {
  width: 18px; height: 18px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 11px; font-weight: 700;
  color: var(--text-tertiary); background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
}
.brq__check.is-ok { color: var(--text-primary); }
.brq__check.is-ok .brq__checkIco {
  color: #fff; background: var(--state-ok); border-color: var(--state-ok);
}
.brq__check.is-info .brq__checkIco {
  color: var(--state-active); background: var(--state-active-bg);
  border-color: var(--state-active-line);
}
.brq__done {
  margin: 12px 0 0; padding-top: 10px; font-size: 12.5px;
  color: var(--state-ok); border-top: 1px solid var(--border-color);
}

/* ---- dialog ---- */
.brq__form { display: flex; flex-direction: column; gap: 14px; }
.brq__formRow { display: flex; gap: 12px; }
.brq__formRow .brq__formField { flex: 1; min-width: 0; }
.brq__formField label {
  display: block; margin-bottom: 4px; font-size: 11px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}

.brq__hint {
  margin: 6px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary);
}
.brq__hint--top { margin: 0 0 14px; }

/* ---- finestra «Converti in prenotazione» ---- */
/* Sezioni con un titolino e campi su griglia: i blocchi in fila, tutti dello
   stesso peso, non si distinguevano l'uno dall'altro. */
.cvt { display: flex; flex-direction: column; gap: 18px; max-height: 62vh; overflow-y: auto; }
.cvt__intro { margin: 0; font-size: 12.5px; line-height: 1.5; color: var(--text-secondary); }
.cvt__sec {
  border: 1px solid var(--border-color); border-radius: 10px; padding: 12px 14px;
  background: var(--bg-primary);
}
.cvt__secT {
  display: flex; align-items: center; gap: 8px; margin: 0 0 10px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.cvt__secTag {
  text-transform: none; letter-spacing: 0; font-weight: 650; font-size: 11px;
  border-radius: 5px; padding: 1px 7px;
  color: var(--svc-nott, var(--text-secondary)); background: var(--bg-secondary);
}
.cvt__grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px 12px;
}
.cvt__grid--app { margin-top: 10px; }
.cvt__fld { min-width: 0; }
.cvt__fld label {
  display: block; margin-bottom: 4px; font-size: 11px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.cvt__hint {
  margin: 8px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary);
}
/* Copertura: un riquadro, non una riga di testo nuda in mezzo ai campi. */
.cvt__cover {
  display: flex; gap: 8px; margin-bottom: 10px; padding: 9px 11px;
  border-radius: 8px; background: var(--bg-secondary);
  font-size: 12.5px; line-height: 1.5; color: var(--text-secondary);
}
.cvt__cover.is-attn { color: var(--state-attn); background: var(--state-attn-bg); }
.cvt__coverIco { flex: none; }

/* ---- sede e prezzo ---- */
.brq__hqAddr { margin: 4px 0 0; font-size: 12px; color: var(--text-secondary); line-height: 1.45; }
.brq__kv--strong b { font-size: 14px; font-variant-numeric: tabular-nums; }
.brq__kv b { font-variant-numeric: tabular-nums; }
.brq__tag {
  margin-left: auto; font-size: 11px; font-weight: 700; border-radius: 5px; padding: 1px 7px;
  color: var(--state-attn); background: var(--bg-secondary);
}
.brq__hqList { display: flex; flex-direction: column; gap: 8px; max-height: 300px; overflow-y: auto; }
.brq__hqOpt {
  display: flex; align-items: flex-start; gap: 10px; width: 100%; text-align: left;
  padding: 10px 12px; border: 1px solid var(--border-color); border-radius: 8px;
  background: var(--bg-primary); cursor: pointer; font-family: inherit;
}
.brq__hqOpt:hover { border-color: var(--brand-violet); }
.brq__hqOpt.is-on { border-color: var(--brand-violet); background: var(--bg-secondary); }
.brq__hqRadio {
  flex: none; width: 14px; height: 14px; margin-top: 2px; border-radius: 50%;
  border: 2px solid var(--border-color);
}
.brq__hqOpt.is-on .brq__hqRadio { border: 4px solid var(--brand-violet); }
.brq__hqTxt { display: flex; flex-direction: column; gap: 2px; font-size: 12.5px; color: var(--text-secondary); }
.brq__hqTxt b { font-size: 13px; color: var(--text-primary); }
</style>
